// Shared Batch types. Kept minimal + structural so either shell's richer
// Lead/Conference types satisfy them without importing app-specific modules.
// Mirrors the portal's `src/api/batchAggregations.ts` + the Lead row shape.

import type { LeadCompleteness, LeadRulesAggregation } from '@viq-admin/types';

/**
 * Height (px) of the pinned CRM stage strip (`crm-tab-toolbar` in
 * `BatchScreen`) — the ONLY number that keeps three independent `position:
 * sticky` consumers from overlapping inside the one scroll container
 * `BatchScreen` hands out via `scrollContainerRef` (fix round 1, p3 Task 6
 * layout follow-up):
 *
 *   1. `BatchScreen`'s own `crm-tab-toolbar` div, pinned at `top: 0`.
 *   2. `BatchTable`'s antd `<Table sticky>` header, anchored to the same
 *      container — antd defaults its sticky header to `top: 0` too, so
 *      without an offset it paints over the strip instead of docking below
 *      it (fix round 1, Critical).
 *   3. `SendView`'s sticky action rail (`crm-send-rail`), whose `top` is
 *      measured from the same container and needs the strip's height added
 *      to `space.lg` or it tucks under the strip (fix round 1, Important).
 *
 * Value: 64 = 48 (the tab nav itself) + 16 (antd's nav bottom margin) — TWO
 * components (fix round 2; do not "simplify" this back to 48, the tab nav
 * height alone, without re-deriving both pieces):
 *
 *   - 48: antd's card-type, `size="large"` `Tabs` nav sizes each tab item to
 *     `cardHeightLG`, which defaults to `controlHeightLG + 8`. Neither
 *     `vendoiqTheme` (`packages/theme/src/vendoiqTheme.ts:162-176`) nor
 *     `BatchScreen` overrides `controlHeight` (antd's stock seed value `32`),
 *     so `controlHeightLG = 32 * 1.25 = 40` and `cardHeightLG = 40 + 8 = 48`.
 *   - 16: top-position `Tabs` also give the nav a `margin: 0 0
 *     ${token.margin}px 0` bottom margin (antd 5.29.3
 *     `node_modules/antd/es/tabs/style/index.js:213,840`, unoverridden
 *     `margin` seed token = 16). `.ant-tabs` is `display: flex`, so this
 *     margin does NOT collapse against anything — it is real box height, not
 *     absorbed whitespace. That margin is also the deliberate visual gap
 *     between the strip and the stage body below it: it must stay, so the
 *     fix is counting it into this constant, not zeroing it out.
 *
 * The toolbar div carries this same number as an explicit `minHeight`, which
 * makes the constant TRUTHFUL rather than an estimate that silently drifts
 * the moment the tabs' own rendered height changes — the div is guaranteed
 * to be at least this tall, so the three consumers above can never disagree
 * about where the strip ends. (`minHeight: 48`, fix round 1's value, sat
 * BELOW the nav's natural 64px height and therefore never actually bound —
 * this 64 is the first value that does.)
 */
export const CRM_STAGE_STRIP_HEIGHT = 64;

export type CaptureType = 'voice' | 'badge' | 'card';

export type TagCategory = 'vip' | 'status' | 'custom';

export interface LeadTag {
  category: TagCategory;
  value: string;
}

/**
 * A tag attached to a person/lead. Structurally identical to {@link LeadTag} —
 * the tag/assignment API modules speak in `PersonTag` (matching the portal's
 * `src/api/tags.ts` shapes) while the table row stores them as `LeadTag`.
 */
export type PersonTag = LeadTag;

/**
 * Predefined relationship-status values offered by the tag picker's STATUS
 * section. Declared locally so the batch package stays self-contained (mirrors
 * the portal's `STATUS_VALUES` in `@viq-admin/types`).
 */
export const STATUS_VALUES = [
  'lead',
  'contact',
  'customer',
  'ex_customer',
  'partner',
  'competitor',
  'unknown',
] as const;
export type StatusValue = (typeof STATUS_VALUES)[number];

/**
 * A company's custom tag definition. Structural subset of the portal's
 * `CustomTagDefinition` — the picker only reads `.value`, so the rest are
 * optional to accept either plane's richer shape without an app import.
 */
export interface CustomTagDefinition {
  value: string;
  company_id?: string;
  created_at?: string;
}

/**
 * Shell-provided tag + assignment API for the editable Batch cells. Each shell
 * wires these to its own plane's endpoints (customer `/v1/admin/...`,
 * super-admin `/v1/superadmin/...`). Responses mirror the portal wire shapes.
 */
export interface BatchTagsApi {
  /** GET tag-definitions for the active scope → `{ definitions }`. */
  fetchTagDefinitions: () => Promise<{ definitions: CustomTagDefinition[] }>;
  /** POST a tag on a lead → the lead's full new tag set. */
  addTag: (
    leadId: string,
    body: { category: string; value: string },
  ) => Promise<{ tags: PersonTag[] }>;
  /** DELETE a tag from a lead → the lead's full new tag set. */
  removeTag: (
    leadId: string,
    category: string,
    value: string,
  ) => Promise<{ tags: PersonTag[] }>;
  /** PATCH the free-text "Assigned to" → the server-confirmed value. */
  setAssignment: (
    leadId: string,
    assignedTo: string | null,
  ) => Promise<{ assignedTo: string | null }>;
}

/** Cross-conference identity match strength surfaced per lead row. */
export type RepeatScanMatch = 'email' | 'name_company' | null;

export interface RepeatScanHistoryEntry {
  event_id: string;
  event_name: string;
  date: string; // ISO yyyy-mm-dd
}

/**
 * Structural subset of the full Lead row consumed by the read-only Batch
 * table. Deliberately loose (most fields optional) so both shells' richer
 * Lead types are assignable without an app-specific import.
 */
export interface BatchLead {
  id: string;
  preview: string;
  title?: string | null;
  companyName?: string | null;
  /**
   * Contact email/phone, projected by `GET /v1/admin/leads`. The backend
   * coerces blank and whitespace-only to `null` at its mapping layer, so
   * `null` is the ONLY "missing" signal a backend that projects the field
   * sends — never test for `''`. A malformed-but-present value (e.g. `bob@`)
   * arrives verbatim and non-null on purpose: the operator needs to see it
   * to fix it.
   *
   * `undefined` is a THIRD state, not a synonym for `null`: these fields are
   * new and the SPA deploys separately from the backend, so a rollback
   * leaves them absent. Absence of information is not a finding — see
   * `BatchTable`'s `blankCellStyle`, which outlines `null` and renders
   * `undefined` plainly.
   */
  email?: string | null;
  phone?: string | null;
  temperature?: string | null;
  tags?: LeadTag[];
  aiSummary?: string | null;
  notesCount?: number;
  openTaskCount?: number;
  createdAt: string;
  captureType: CaptureType;
  repName?: string;
  repOid: string;
  assignedTo?: string | null;
  deletedAt?: string | null;
  repeatScanMatch?: RepeatScanMatch;
  repeatScanHistory?: RepeatScanHistoryEntry[];
  /**
   * Company lead rules (spec 2026-09-14 §2.2). `null` = company off;
   * `undefined` = an older backend that doesn't project it — never a finding.
   */
  completeness?: LeadCompleteness | null;
  /** Last CRM send; null if never. Sent leads are never flagged. */
  crmSentAt?: string | null;
  /**
   * The stored split name (`persons.first_name` / `last_name`, verbatim;
   * `''` = not captured). `preview` stays the display name. Prefills the Name
   * cell's First / Last popover (spec 2026-09-17 §1.6). `undefined` is an
   * older backend that doesn't project them — and so has no
   * `PATCH …/fields` route either (both are viq-backend#117): inline editing
   * stays off until a loaded row carries them (`rowsCarryLeadFields`). The
   * grid never splits `preview` into a name (plan D2).
   */
  firstName?: string;
  lastName?: string;
  /**
   * The lead's newest rep note, which the grid's Notes column shows and edits
   * (port plan step 5). `null` = the lead has none. Lead-move ("system")
   * notes never count here, though `notesCount` still counts them (D11).
   * `undefined` is an older backend that doesn't project it: the grid then
   * shows no Notes column at all.
   */
  latestNote?: BatchNote | null;
}

/**
 * One rep note as list rows carry it (`latestNote`), and as the note API
 * hands a saved one back. `serverVersion` is the note's CURRENT version: an
 * edit sends it back as `expectedVersion`, and the server 409s when the note
 * has changed since.
 */
export interface BatchNote {
  id: string;
  text: string;
  serverVersion: number;
  /** ISO 8601, like `BatchLead.createdAt`. */
  createdAt: string;
}

/**
 * Shell-provided note API for the grid's Notes column. Its PRESENCE is what
 * makes the column editable, on the Active tab only, independently of
 * `readOnly` (the `leadFieldsApi` pattern). The portal wires it for
 * `manage:Lead`; the console for `can('update', 'Lead')`.
 *
 * Both resolve to the saved note in `latestNote` shape. Errors reach the cell
 * as whatever the shell's fetch throws; it reads `statusCode` and `message`
 * off them.
 */
export interface BatchLeadNotesApi {
  /** POST …/leads/:id/notes `{ text }` → the new note (the lead had none). */
  createNote: (leadId: string, text: string) => Promise<BatchNote>;
  /**
   * PATCH …/leads/:id/notes/:noteId `{ text, expectedVersion }`, with `note`'s
   * id and `serverVersion` → the edited note. 409 when someone else changed
   * it first.
   */
  editNote: (leadId: string, note: BatchNote, text: string) => Promise<BatchNote>;
}

// ── Inline lead editing (spec 2026-09-17, amended 2026-09-30) ──────────────

/**
 * Body of PATCH …/leads/:id/fields: only the fields being changed, blanks as
 * `''`. Never `displayName` (the server derives it from first + last and
 * 400s the key) and never `expectedVersion` (per-field last-write-wins).
 */
export interface LeadFieldsPatch {
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  /** `''`, `'hot'`, `'warm'` or `'cold'`. */
  temperature?: string;
}

/** One editable field of {@link LeadFieldsPatch}. */
export type LeadFieldsKey = keyof LeadFieldsPatch;

/**
 * The 200 body's `lead`: the updated row in LIST-ROW shape (`preview` is the
 * derived display name, `companyName` the contact's employer, a blank email
 * or phone `null`) plus `completeness` / `crmSentAt`, so it merges straight
 * into a table row. Loose on purpose, like `BatchLead`: the wire also carries
 * `status`, `serverVersion` and `updatedAt`, which the grid doesn't read.
 */
export type BatchLeadFieldsResult = Partial<BatchLead> & { id: string };

/**
 * Shell-provided inline-edit API for the Batch grid's Name, Company, Title,
 * Email, Phone and Temp cells. Its PRESENCE is what makes those cells
 * editable (spec D10), separately from `readOnly`, which keeps governing tags
 * and assignment — once the loaded rows show the backend has the route
 * (`rowsCarryLeadFields`). The portal wires it for `manage:Lead`; the console
 * for `can('update', 'Lead')`.
 *
 * Errors reach the grid as whatever the shell's fetch throws; it reads
 * `statusCode`, `message`, `errors` (field errors) and `retryAfter` (seconds)
 * off them, the shape both planes' fetch layers throw.
 */
export interface BatchLeadFieldsApi {
  /** PATCH …/leads/:id/fields → `{ lead }`, the updated row fields. */
  updateFields: (leadId: string, patch: LeadFieldsPatch) => Promise<{ lead: BatchLeadFieldsResult }>;
}

export interface LeadsPage {
  leads: BatchLead[];
  nextCursor: string | null;
}

/** Ported verbatim from the portal's `src/api/batchAggregations.ts`. */
export interface AggregationsResponse {
  kpi: {
    total_scans: number;
    total_notes: number;
    total_tasks: number;
    meetings_booked: number;
    /** The event's scans goal (Event Plan, not built yet). Optional for
     *  deploy skew: no backend sends it today, and null, absent or 0 all
     *  keep the plain Scans tile. */
    scans_goal?: number | null;
  };
  coverage: {
    repeat_visitors: number;
    vip_count: number;
    unique_scans: number;
    unique_companies: number;
  };
  /** Notes readiness for the Notes tile; null/undefined → neutral tile. */
  leadRules?: LeadRulesAggregation | null;
}

/** Conference option supplied by the shell (super-admin lists all tenants). */
export interface BatchConference {
  id: string;
  name: string;
  location?: string | null;
  /** `YYYY-MM-DD`. Drives the rail's active/upcoming/past grouping + default
   *  selection. Optional so undated shells still render (events sink to past). */
  startDate?: string;
  endDate?: string;
}

/** Shell-provided leads fetcher — cursor-paginated, read-only for Batch-1. */
export type BatchLeadsFetcher = (args: {
  conferenceId: string;
  cursor?: string;
  limit?: number;
  deleted?: boolean;
}) => Promise<LeadsPage>;

/** Shell-provided aggregations fetcher for the selected conference. */
export type BatchAggregationsFetcher = (
  conferenceId: string,
) => Promise<AggregationsResponse>;

// ── Export (CSV) ────────────────────────────────────────────────────────────
// Mirrors the portal's export UX (`src/views/BatchScreen/ExportDialog.tsx` +
// `src/api/exportWatermark.ts`). Modes drive the server-side watermark; xlsx is
// deferred server-side (422 in v1), so the dialog offers csv only.

export type ExportMode = 'all' | 'net_new' | 'updated_since';
export type ExportFormat = 'csv' | 'xlsx';

/** One selectable column, as the server describes it. */
export interface ExportColumnDescriptor {
  key: string;
  header: string;
}

/** The catalog plus this user's saved selection. */
export interface ExportColumnPrefs {
  availableColumns: ExportColumnDescriptor[];
  hiddenColumns: string[];
  includeDiscovery: boolean;
  includeDiscoverySummary: boolean;
}

/**
 * Shell-provided export API for the shared Batch workspace. Each shell wires
 * these to its own plane's endpoints (customer `/v1/admin/...`, super-admin
 * `/v1/superadmin/...`).
 */
export interface BatchExportApi {
  /**
   * Performs the download: fetch CSV, read the `Content-Disposition` filename,
   * and trigger a browser save. A successful download also advances the
   * (caller, conference) watermark server-side ("download = mark exported").
   */
  downloadExport: (args: {
    conferenceId: string;
    mode: ExportMode;
    format: 'csv';
    hideColumns?: string[];
    includeDiscovery?: boolean;
    includeDiscoverySummary?: boolean;
  }) => Promise<void>;

  /** The column catalog + this user's saved selection. */
  getColumnPrefs: () => Promise<ExportColumnPrefs>;

  /** Persist the selection. Called on Export, never on Cancel. */
  saveColumnPrefs: (prefs: {
    hiddenColumns: string[];
    includeDiscovery: boolean;
    includeDiscoverySummary: boolean;
  }) => Promise<void>;

  /**
   * Explicit "mark as exported" — advances the (caller, conference) watermark
   * (the net_new/updated_since baseline) to now() without producing a download.
   */
  markExported: (conferenceId: string) => Promise<void>;
}

// ── Salesforce export (super-admin, cross-tenant, per-company) ────────────────
// The console has NO company of its own, so the shared dialog first picks a
// TARGET COMPANY (one of the SF-connected tenants), shows THAT company's leads
// + their per-lead sync state, and — because this is a privileged cross-tenant
// write — gates the push behind a type-`CONFIRM` modal. Only the super-admin
// shell wires `salesforceApi`; the portal leaves it undefined and the
// affordance hides (its own CRM integration is `crmApi`, below).

export type SalesforceExportMode = 'new' | 'resync_all';

export interface SalesforceExportResult {
  campaignId: string;
  total: number;
  synced: number;
  skipped: number;
  failed: { personId: string; reason: string }[];
  /** Leads refused by company lead rules (notes below minimum); absent from older backends. */
  incomplete?: { personId: string; have: number; need: number }[];
}

/** Per-lead Salesforce sync state for a (conference, company) pair. */
export interface SalesforceMember {
  personId: string;
  syncedAt: string | null;
  syncError: string | null;
}

/** A SF-connected target company the operator can push leads into. */
export interface SalesforceCompany {
  companyId: string;
  companyName: string;
}

/**
 * Shell-provided, CROSS-TENANT Salesforce export API for the shared Batch
 * workspace. Wired by the super-admin console (`/v1/superadmin/...`). Every call
 * carries the chosen `targetCompanyId` so the backend routes the write to that
 * tenant's own Salesforce org.
 *
 * `kind` is OPTIONAL here (absent = cross-tenant) so the console's existing
 * plain object literal — which sets no `kind` — still assigns to this shape
 * unchanged. The discriminant only needs to be *present* on the own-company
 * variant.
 */
export interface CrossTenantSalesforceApi {
  /** Discriminant. Optional/absent → cross-tenant (the console's existing shape). */
  kind?: 'cross-tenant';
  /**
   * Whether AT LEAST ONE tenant has Salesforce connected (fail-closed, mirrors
   * the own-company variant's `connected`). Missing/false hides the affordance
   * entirely — there'd be no valid target company to pick in the export dialog.
   * The shell computes this from the same `listSalesforceCompanies()` result
   * (length > 0) it feeds the dialog's company picker.
   */
  connected?: boolean;
  /** SF-connected companies eligible as push targets (`crmSource: 'salesforce'`). */
  listSalesforceCompanies: () => Promise<SalesforceCompany[]>;
  /** Per-lead SF sync state for a target company's leads at this conference. */
  fetchMembers: (
    conferenceId: string,
    companyId: string,
  ) => Promise<SalesforceMember[]>;
  /** The target company's leads for this conference, offered for selection. */
  fetchCompanyLeads: (
    conferenceId: string,
    companyId: string,
  ) => Promise<BatchLead[]>;
  /** Push the selected leads into the target company's Salesforce. */
  exportToSalesforce: (
    conferenceId: string,
    args: {
      targetCompanyId: string;
      personIds: string[];
      mode: SalesforceExportMode;
    },
  ) => Promise<SalesforceExportResult>;
}

// NOTE: the customer portal's OWN-COMPANY Salesforce variant (`OwnCompanySalesforceApi`,
// `OwnCompanySalesforceExportDialog`, and BatchTable's per-row selection +
// status column) was retired in p3 Task 6 — superseded by the CRM merge queue
// (`./crm/types.ts`'s `CrmQueueApi`, re-exported below). The console's
// cross-tenant variant (`CrossTenantSalesforceApi`, above) is untouched.

// ── CRM merge queue (p3 Clean/Match/Send) ──────────────────────────────────
// Re-exported here so BatchScreen (and any consumer already importing shell-
// facing shapes from `./types`) has one import path rather than a second into
// `./crm/types`.
export type { CrmQueueApi } from './crm/types';

// ── Move leads (super-admin, cross-tenant) ─────────────────────────────────

/** One conference a lead can be moved into. */
export interface MoveDestination {
  id: string;
  name: string;
}

/** What POST /v1/superadmin/leads/move returns on success. */
export interface MoveLeadsResult {
  moved: number;
  /**
   * Discovery answers that stayed recorded against the SOURCE conference's
   * questions. The move does not delete or remap them: a conference's
   * questions are private copies, so there is no equivalent row at the
   * destination to point them at. Was `discoveryResponsesDropped` while the
   * backend hard-deleted them; same number, opposite meaning.
   */
  discoveryAnswersCarriedOver: number;
  notesWritten: number;
}

/** What the move dialog learns before a move (portal only). */
export interface MovePreview {
  /** Selected leads the caller may move. */
  leadCount: number;
  /** How many of them were already sent to a CRM. */
  crmSentCount: number;
}

/**
 * Injected to enable moving leads between conferences. Wired by the console
 * always, and by the customer portal only for companies granted the
 * lead-move flag (company admins only).
 */
export interface BatchMoveApi {
  /**
   * The company whose leads are on screen, or null when the operator has not
   * narrowed to one tenant. Null disables the affordance: a super-admin sees
   * every tenant, and BatchLead carries no companyId to infer one from, so
   * offering a destination list without a company could surface another
   * client's conferences.
   */
  companyId: string | null;
  /** Conferences the company is granted, non-archived. Server-scoped. */
  listDestinations: (companyId: string) => Promise<MoveDestination[]>;
  moveLeads: (leadIds: string[], destinationConferenceId: string) => Promise<MoveLeadsResult>;
  /**
   * Portal only. When present, the dialog calls it on open and warns about
   * leads already sent to a CRM (a move leaves them in the old conference's
   * campaign or list there). The console doesn't wire it.
   */
  previewMove?: (leadIds: string[]) => Promise<MovePreview>;
}

// ── Bulk lead actions (customer portal) ────────────────────────────────────

/**
 * Injected by the customer portal to give the leads table's row selection
 * something to do: tag / assign / delete the selected leads, and restore them
 * on the Deleted tab. The portal's checkboxes were removed alongside the
 * own-company Salesforce dialog in p3 Task 6; this is what switches them back
 * on, the way `BatchMoveApi` does for the console.
 *
 * The callbacks are PER LEAD on purpose: the customer plane has no bulk
 * endpoint — every lead mutation is `/v1/admin/leads/:id/...`. `runBulk`
 * (./bulkRunner) fans them out with bounded concurrency and a rate-limit
 * backoff rather than a `Promise.all`, because the write bucket is 60
 * requests per minute per caller.
 *
 * Absent in the console, which is what keeps the affordance portal-only.
 */
export interface BatchBulkApi {
  /**
   * Whether the caller may mutate leads (the portal's `manage:Lead`,
   * company_admin only). False withholds the whole affordance — the checkbox
   * column included, because a selection whose every action is denied is
   * worse than no selection at all.
   */
  canManage: boolean;
  addTag: (leadId: string, body: { category: string; value: string }) => Promise<void>;
  removeTag: (leadId: string, category: string, value: string) => Promise<void>;
  setAssignment: (leadId: string, assignedTo: string | null) => Promise<void>;
  /** Soft delete (archive). Optional → hides "Delete selected". */
  archiveLead?: (leadId: string) => Promise<void>;
  /** Optional → hides "Restore selected" on the Deleted tab. */
  restoreLead?: (leadId: string) => Promise<void>;
}
